'use client'

import { useState } from 'react'
import { type SceneParams, defaultParams } from './scene-params'

interface Props {
  params: SceneParams
  setParams: (p: SceneParams) => void
}

function set<K extends keyof SceneParams>(
  params: SceneParams,
  setParams: (p: SceneParams) => void,
  key: K,
  value: SceneParams[K]
) {
  setParams({ ...params, [key]: value })
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (v: number) => void
}) {
  return (
    <div className="flex items-center gap-2 w-full">
      <span className="text-[10px] text-coldwing-gray/60 w-16 shrink-0 uppercase tracking-wide truncate">
        {label}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 h-[3px] accent-coldwing-accent cursor-pointer min-w-0"
      />
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-16 bg-black/40 text-coldwing-light text-[10px] font-mono px-1.5 py-0.5 border border-white/10 rounded focus:outline-none focus:border-coldwing-accent/60 shrink-0"
      />
    </div>
  )
}

function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex items-center gap-2 w-full">
      <span className="text-[10px] text-coldwing-gray/60 w-16 shrink-0 uppercase tracking-wide">
        {label}
      </span>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-5 w-8 cursor-pointer rounded border-0 bg-transparent p-0 shrink-0"
      />
      <span className="text-[10px] text-coldwing-gray/40 font-mono">{value}</span>
    </div>
  )
}

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border border-white/[0.06] rounded-md overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
      >
        <span className="text-[9px] font-bold tracking-[0.2em] text-coldwing-accent uppercase">
          {title}
        </span>
        <span className="text-[9px] text-coldwing-gray/40">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="px-3 py-2.5 space-y-2 bg-black/20">
          {children}
        </div>
      )}
    </div>
  )
}

export default function DebugPanel({ params, setParams }: Props) {
  const [open, setOpen] = useState(true)
  const [copied, setCopied] = useState(false)
  const [modalJson, setModalJson] = useState<string | null>(null)

  const s = <K extends keyof SceneParams>(key: K, value: SceneParams[K]) =>
    set(params, setParams, key, value)

  function handleSave() {
    const output = JSON.stringify(params, null, 2)
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(output)
        .then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 2500)
        })
        .catch(() => setModalJson(output))
    } else {
      setModalJson(output)
    }
  }

  function handleReset() {
    setParams(defaultParams)
  }

  return (
    <>
      {modalJson && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 pointer-events-auto"
          onClick={() => setModalJson(null)}
        >
          <div
            className="bg-coldwing-dark border border-coldwing-accent/30 rounded-lg p-5 w-[520px] max-w-[92vw] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <span className="text-[11px] font-bold tracking-[0.2em] text-coldwing-accent uppercase">
                Parâmetros da Cena
              </span>
              <button
                onClick={() => setModalJson(null)}
                className="text-coldwing-gray/50 hover:text-coldwing-gray text-lg leading-none"
              >
                ✕
              </button>
            </div>
            <textarea
              readOnly
              value={modalJson}
              rows={18}
              onFocus={(e) => e.target.select()}
              className="w-full bg-black/60 text-coldwing-light text-[11px] font-mono p-3 border border-white/10 rounded resize-none focus:outline-none focus:border-coldwing-accent/40"
            />
            <p className="text-[10px] text-coldwing-gray/40 mt-2">
              Clique na área acima → Ctrl+A → Ctrl+C para copiar
            </p>
          </div>
        </div>
      )}

      {/* Sidebar toggle button (always visible) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed top-3 left-3 z-50 pointer-events-auto flex items-center gap-1.5 px-2.5 py-1.5 bg-black/80 border border-white/10 rounded-md backdrop-blur-sm hover:border-coldwing-accent/40 transition-colors"
        >
          <span className="text-[9px] font-bold tracking-[0.2em] text-coldwing-accent uppercase">
            ⚙ Debug
          </span>
        </button>
      )}

      {/* Sidebar panel */}
      {open && (
        <div className="fixed top-0 left-0 h-screen z-50 pointer-events-auto flex flex-col w-64 bg-black/90 border-r border-white/10 backdrop-blur-sm select-none">

          {/* Header */}
          <div className="flex items-center justify-between px-3 h-10 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold tracking-[0.25em] text-coldwing-accent uppercase">
                ⚙ Scene Debug
              </span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-coldwing-gray/40 hover:text-coldwing-gray transition-colors text-xs"
              title="Fechar painel"
            >
              ✕
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-3 space-y-2">

            <Section title="Model">
              <Slider label="Scale"  value={params.modelScale} min={0.1} max={50}  step={0.05} onChange={(v) => s('modelScale', v)} />
              <Slider label="Pos X"  value={params.modelPosX}  min={-30} max={30}  step={0.05} onChange={(v) => s('modelPosX', v)} />
              <Slider label="Pos Y"  value={params.modelPosY}  min={-30} max={30}  step={0.05} onChange={(v) => s('modelPosY', v)} />
              <Slider label="Pos Z"  value={params.modelPosZ}  min={-30} max={30}  step={0.05} onChange={(v) => s('modelPosZ', v)} />
            </Section>

            <Section title="Camera">
              <Slider label="Pos X"    value={params.camX}        min={-30}  max={30}   step={0.05} onChange={(v) => s('camX', v)} />
              <Slider label="Pos Y"    value={params.camY}        min={-10}  max={30}   step={0.05} onChange={(v) => s('camY', v)} />
              <Slider label="Pos Z"    value={params.camZ}        min={-30}  max={50}   step={0.05} onChange={(v) => s('camZ', v)} />
              <Slider label="FOV"      value={params.camFov}      min={20}   max={120}  step={0.5}  onChange={(v) => s('camFov', v)} />
              <Slider label="Target X" value={params.camTargetX}  min={-30}  max={30}   step={0.05} onChange={(v) => s('camTargetX', v)} />
              <Slider label="Target Y" value={params.camTargetY}  min={-20}  max={20}   step={0.05} onChange={(v) => s('camTargetY', v)} />
              <Slider label="Target Z" value={params.camTargetZ}  min={-30}  max={30}   step={0.05} onChange={(v) => s('camTargetZ', v)} />
            </Section>

            <Section title="Fog">
              <ColorRow label="Color" value={params.fogColor} onChange={(v) => s('fogColor', v)} />
              <Slider   label="Near"  value={params.fogNear}  min={0}   max={100} step={0.2} onChange={(v) => s('fogNear', v)} />
              <Slider   label="Far"   value={params.fogFar}   min={0.5} max={200} step={0.2} onChange={(v) => s('fogFar', v)} />
            </Section>

            <Section title="Mist">
              <Slider label="Opacity" value={params.mistOpacity} min={0}   max={1}   step={0.01}  onChange={(v) => s('mistOpacity', v)} />
              <Slider label="Speed"   value={params.mistSpeed}   min={0}   max={3}   step={0.05}  onChange={(v) => s('mistSpeed', v)} />
              <Slider label="Size"    value={params.mistSize}    min={0.1} max={10}  step={0.1}   onChange={(v) => s('mistSize', v)} />
            </Section>

            <Section title="Hemisphere" defaultOpen={false}>
              <ColorRow label="Sky"    value={params.hemSkyColor}    onChange={(v) => s('hemSkyColor', v)} />
              <ColorRow label="Ground" value={params.hemGroundColor} onChange={(v) => s('hemGroundColor', v)} />
              <Slider   label="Int"    value={params.hemIntensity}   min={0} max={5} step={0.02} onChange={(v) => s('hemIntensity', v)} />
            </Section>

            <Section title="Dir Light 1" defaultOpen={false}>
              <ColorRow label="Color" value={params.dir1Color}     onChange={(v) => s('dir1Color', v)} />
              <Slider   label="Int"   value={params.dir1Intensity} min={0} max={10}  step={0.05} onChange={(v) => s('dir1Intensity', v)} />
              <Slider   label="X"     value={params.dir1X}         min={-50} max={50} step={0.2} onChange={(v) => s('dir1X', v)} />
              <Slider   label="Y"     value={params.dir1Y}         min={-50} max={50} step={0.2} onChange={(v) => s('dir1Y', v)} />
              <Slider   label="Z"     value={params.dir1Z}         min={-50} max={50} step={0.2} onChange={(v) => s('dir1Z', v)} />
            </Section>

            <Section title="Dir Light 2" defaultOpen={false}>
              <ColorRow label="Color" value={params.dir2Color}     onChange={(v) => s('dir2Color', v)} />
              <Slider   label="Int"   value={params.dir2Intensity} min={0} max={10}  step={0.05} onChange={(v) => s('dir2Intensity', v)} />
              <Slider   label="X"     value={params.dir2X}         min={-50} max={50} step={0.2} onChange={(v) => s('dir2X', v)} />
              <Slider   label="Y"     value={params.dir2Y}         min={-50} max={50} step={0.2} onChange={(v) => s('dir2Y', v)} />
              <Slider   label="Z"     value={params.dir2Z}         min={-50} max={50} step={0.2} onChange={(v) => s('dir2Z', v)} />
            </Section>

            <Section title="Snow">
              <Slider label="Count"   value={params.snowCount}   min={500}  max={20000} step={500}   onChange={(v) => s('snowCount', Math.round(v))} />
              <Slider label="Speed"   value={params.snowSpeed}   min={0}    max={5}     step={0.02}  onChange={(v) => s('snowSpeed', v)} />
              <Slider label="Size"    value={params.snowSize}    min={0.01} max={0.3}   step={0.002} onChange={(v) => s('snowSize', v)} />
              <Slider label="Opacity" value={params.snowOpacity} min={0}    max={1}     step={0.01}  onChange={(v) => s('snowOpacity', v)} />
            </Section>

            <Section title="Skybox">
              <Slider label="Rotate" value={params.skyboxRotation} min={-3.14} max={3.14} step={0.01} onChange={(v) => s('skyboxRotation', v)} />
              <Slider label="Blur"   value={params.skyboxBlur}     min={0}     max={1}    step={0.01}  onChange={(v) => s('skyboxBlur', v)} />
            </Section>

            <Section title="Effects">
              <Slider label="Bloom"    value={params.bloomIntensity}   min={0}   max={3}  step={0.01} onChange={(v) => s('bloomIntensity', v)} />
              <Slider label="B.Thresh" value={params.bloomThreshold}   min={0}   max={1}  step={0.01} onChange={(v) => s('bloomThreshold', v)} />
              <Slider label="DOF Dist" value={params.dofFocusDistance} min={0}   max={80} step={0.1}  onChange={(v) => s('dofFocusDistance', v)} />
              <Slider label="DOF Rng"  value={params.dofFocusRange}    min={0}   max={30} step={0.1}  onChange={(v) => s('dofFocusRange', v)} />
              <Slider label="Bokeh"    value={params.dofBokeh}         min={0}   max={5}  step={0.05} onChange={(v) => s('dofBokeh', v)} />
              <Slider label="Vignette" value={params.vignetteDarkness} min={0}   max={1}  step={0.01} onChange={(v) => s('vignetteDarkness', v)} />
            </Section>

          </div>

          {/* Footer actions */}
          <div className="shrink-0 border-t border-white/10 px-3 py-2.5 flex gap-2">
            <button
              onClick={handleReset}
              className="flex-1 text-[10px] py-1.5 rounded border border-white/10 text-coldwing-gray/60 hover:text-coldwing-gray hover:border-white/20 transition-colors"
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              className={`flex-1 text-[10px] py-1.5 rounded font-semibold tracking-wider uppercase transition-all ${
                copied
                  ? 'bg-green-500/80 text-white border border-green-400/50'
                  : 'bg-coldwing-accent/20 text-coldwing-accent border border-coldwing-accent/40 hover:bg-coldwing-accent/30'
              }`}
            >
              {copied ? '✓ Copiado!' : 'Salvar'}
            </button>
          </div>

        </div>
      )}
    </>
  )
}
